import React, { useEffect, useState } from "react";
import AccordionCustom from "../../legacy/Accordion";
import { connect, useDispatch } from "react-redux";
import { getExamRequestsByPatient, createExamRequest } from "../../../redux/examsRequest/actions";
import { getStoragePatient, getUserData } from "../../../utils/storageUtils";
import { getActsPriceInsurerByInsurer } from "../../../redux/acts/actions";
import { Combobox } from "@/components/ui/combobox";
import { openProtectedFile } from "../../../utils/openProtectedFile";
import useActsLookup from "hooks/queries/useActsLookup";

const storagePatient = getStoragePatient();
const storageUser = getUserData();

// Aligné sur le helper de TabTwo : le back renvoie `createdAt` au format
// ISO complet ; on n'affiche que la date pour rester lisible dans le
// titre de l'accordéon.
const formatDateOnly = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

// `actsTypeName` est un enum back (`CONSULTATION` | `EXAM`). Aligné sur
// le mapping utilisé côté TabThree (calculs facture).
const ACTS_TYPE_LABELS = {
  CONSULTATION: "Consultation",
  EXAM: "Examen",
};

const TabFour = ({
  current_patient: curr_patient,
  acts_price_insurer,
  examsRequests,
}) => {
  const dispatch = useDispatch();
  // Acts : petite table cachée 30 min côté client (cf. useActsLookup).
  // `acts_price_insurer` reste en redux — c'est de la donnée tarifaire
  // par patient/insurer, pas un select.
  const { data: acts = [] } = useActsLookup();
  const [openForm, setOpenForm] = useState(false);
  const current_patient = Object.keys(curr_patient).length
    ? curr_patient
    : storagePatient;

  const [form, setForm] = useState({
    actsId: [],
    patientId: current_patient?.id,
    doctorId: storageUser?.user?.id,
  });

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      doctorId: storageUser?.user?.id,
      patientId: current_patient?.id,
    }));
  }, [current_patient?.id]);

  useEffect(() => {
    if (current_patient?.id != null) {
      dispatch(getExamRequestsByPatient(current_patient.id));
    }
    if (current_patient?.insurer?.id != null) {
      dispatch(getActsPriceInsurerByInsurer(current_patient.insurer.id));
    }
  }, [dispatch, current_patient?.id, current_patient?.insurer?.id]);

  const generateId = (data) => {
    if (data.length === 0) return 0;
    return Number(data[data.length - 1].id) + 1;
  };

  const actItem = { id: 0 };

  const handleAddInvoiceItem = (e) => {
    e.preventDefault();
    setForm((prev) => ({
      ...prev,
      actsId: [...prev.actsId, { ...actItem, id: generateId(prev.actsId) }],
    }));
  };

  const handleRemoveInvoiceItem = (e, idx) => {
    e.preventDefault();
    setForm((prev) => ({
      ...prev,
      actsId: prev.actsId.filter((i) => i.id !== idx),
    }));
  };

  // Le saga attend `actsId: number[]` ; on aplatit la liste éditée
  // (qui porte aussi label/value pour l'UI Combobox) avant POST.
  const mapReturnRequests = (data) => data.map((item) => item.value).filter(Boolean);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(
      createExamRequest({ ...form, actsId: mapReturnRequests(form.actsId) })
    );
  };

  const adaptSelectActsOptions = (data) =>
    (data || []).map((item) => ({
      value: item?.id,
      label: item?.label,
      ...item,
    }));

  // `getExamRequestsByPatient(current_patient.id)` est déjà scopé côté
  // back — pas de filtre client à refaire. L'ancienne version filtrait
  // sur `er.patient.id`, qui n'existe plus depuis que le back renvoie
  // une réponse plate (`patientId`, `patientFirstName`… au top level).
  const filteredExams = (examsRequests || [])
    .slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div className="space-y-6" data-tab="bons">
      {/* Action header — eyebrow + CTA aligné droite */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
            Demandes d'examens
          </p>
          <h2 className="mt-1 text-lg font-semibold text-slate-800">
            Examens prescrits
          </h2>
          <p className="text-sm text-slate-500">
            {filteredExams.length} demande{filteredExams.length > 1 ? "s" : ""} pour ce patient
          </p>
        </div>
        {!openForm && (
          <button
            type="button"
            onClick={() => setOpenForm(true)}
            className="group/btn relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
            />
            <i className="bx bx-plus text-lg" />
            Nouvelle demande
          </button>
        )}
      </div>

      {/* Form de création */}
      {openForm && (
        <form className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">
              Nouvelle demande d'examen
            </h3>
            <button
              type="button"
              onClick={() => setOpenForm(false)}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
              aria-label="Fermer"
            >
              <i className="bx bx-x text-xl" />
            </button>
          </div>

          {form.actsId.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-8 text-center text-sm text-slate-500">
              Aucun acte ajouté. Cliquez sur « Ajouter un acte » pour commencer.
            </div>
          ) : (
            <div className="space-y-3">
              {form.actsId.map((item, idx) => (
                <div
                  key={item.id}
                  className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/40 p-3 sm:flex-row sm:items-center"
                >
                  <span className="text-xs font-medium text-slate-400 sm:w-8">
                    #{idx + 1}
                  </span>
                  <div className="flex-1">
                    <Combobox
                      options={adaptSelectActsOptions(acts)}
                      value={item?.value ?? null}
                      placeholder="Sélectionner un acte"
                      searchPlaceholder="Rechercher un acte..."
                      className="w-full"
                      onChange={(data) => {
                        setForm((prev) => {
                          const next = [...prev.actsId];
                          next[idx] = {
                            ...next[idx],
                            ...data,
                            actsId: data?.value,
                          };
                          return { ...prev, actsId: next };
                        });
                      }}
                    />
                  </div>
                  <button
                    onClick={(e) => handleRemoveInvoiceItem(e, item.id)}
                    type="button"
                    className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
                    aria-label="Supprimer"
                  >
                    <i className="bx bx-trash text-lg" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <button
              onClick={handleAddInvoiceItem}
              type="button"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-50 hover:border-emerald-200"
            >
              <i className="bx bx-plus-circle text-base" />
              Ajouter un acte
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOpenForm(false)}
                className="inline-flex items-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={form.actsId.length === 0}
                onClick={(e) => {
                  setOpenForm(false);
                  handleSubmit(e);
                }}
                className="group/btn relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
                />
                <i className="bx bx-check text-lg" />
                Enregistrer
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Liste des demandes */}
      <div className="space-y-3">
        {filteredExams.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/40 px-6 py-12 text-center">
            <i className="bx bx-clipboard text-4xl text-slate-300" />
            <p className="mt-2 text-sm font-medium text-slate-600">
              Aucune demande d'examen
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Les demandes créées apparaîtront ici.
            </p>
          </div>
        ) : (
          filteredExams.map((examRequest) => {
            const actsCount = examRequest?.actsList?.length || 0;
            return (
              <AccordionCustom
                key={examRequest.id}
                title={
                  <div className="flex items-center gap-3 flex-wrap min-w-0">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-sm ring-2 ring-white">
                      <i className="bx bx-clipboard text-base" />
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                        Demande d'examens
                      </span>
                      <span className="text-sm font-semibold text-slate-800 truncate">
                        {formatDateOnly(examRequest?.createdAt)}
                      </span>
                    </div>
                    {examRequest?.doctorFirstName && (
                      <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-600">
                        <i className="bx bx-plus-medical text-sm text-emerald-600" />
                        Dr. {examRequest.doctorFirstName} {examRequest.doctorLastName}
                      </span>
                    )}
                    {actsCount > 0 && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-100 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                        <i className="bx bx-list-ul text-sm" />
                        {actsCount} acte{actsCount > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                }
              >
                <div className="px-4 py-5 sm:px-6 bg-slate-50/40 space-y-4">
                  <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <i className="bx bx-clipboard text-lg text-emerald-600" />
                        <h5 className="text-sm font-semibold text-slate-800">
                          Actes prescrits
                        </h5>
                        {actsCount > 0 && (
                          <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-emerald-50 border border-emerald-100 text-[10px] font-semibold text-emerald-700">
                            {actsCount}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!examRequest?.id) return;
                          openProtectedFile(
                            `/request-exam/exam-request-pdf/${examRequest.id}`,
                            {
                              loadingMessage:
                                "Téléchargement de la demande d'examen…",
                            }
                          );
                        }}
                        disabled={!examRequest?.id}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="bx bx-download text-base" />
                        PDF
                      </button>
                    </header>
                    {actsCount > 0 ? (
                      <ul className="divide-y divide-slate-100">
                        {examRequest.actsList.map((item, idx) => {
                          const typeLabel =
                            ACTS_TYPE_LABELS[item?.actsTypeName] ||
                            item?.actsTypeName;
                          return (
                            <li
                              key={idx}
                              className="flex items-start gap-3 px-4 py-3 hover:bg-emerald-50/30 transition-colors"
                            >
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                                <i className="bx bx-clipboard text-base" />
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-slate-800 truncate">
                                    {item?.label || "Acte"}
                                  </span>
                                  {typeLabel && (
                                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                      {typeLabel}
                                    </span>
                                  )}
                                </div>
                                {item?.description ? (
                                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">
                                    {item.description}
                                  </p>
                                ) : null}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="px-4 py-6 text-center text-xs text-slate-400">
                        Aucun acte demandé.
                      </p>
                    )}
                  </section>
                </div>
              </AccordionCustom>
            );
          })
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({
  usersReducer,
  actsReducer,
  examsRequestsReducer,
}) => ({
  current_patient: usersReducer.current_patient,
  acts_price_insurer: actsReducer.acts_price_insurer,
  examsRequests: examsRequestsReducer.examsRequests,
});

export default connect(mapStateToProps)(TabFour);
